"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { Check, ClipboardCopy, Database, LoaderCircle, Mail, RefreshCw, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export type MemberProjectRuntimeProfile = {
  configured: boolean;
  project_ref?: string;
  project_url?: string;
  publishable_key?: string;
  connection_status?: "NOT_CONFIGURED" | "CONFIGURED" | "CONNECTED" | "VERIFYING" | "VERIFIED" | "ERROR" | "DISABLED";
  schema_version?: string | null;
};

type TenantHealth = {
  ok?: boolean;
  schema_version?: string;
  movie_count?: number;
  concept_run_count?: number;
  character_count?: number;
  location_count?: number;
  file_count?: number;
  enterprise_checks?: {
    missing_required_objects?: number;
    protected_tables_without_rls?: number;
    anon_protected_table_grants?: number;
    private_storage_bucket?: boolean;
  };
};

type TenantProfile = {
  configured?: boolean;
  central_user_id?: string;
  member_email?: string;
  tenant_project_ref?: string;
  connection_status?: string;
  schema_version?: string | null;
  tenant_user_id?: string | null;
  verified_email?: string | null;
  enrollment_status?: "NOT_CONFIGURED" | "PENDING" | "VERIFIED" | "REVOKED" | "ERROR" | "DISABLED";
  enrolled_at?: string | null;
  last_verified_at?: string | null;
  revoked_at?: string | null;
  last_error?: string | null;
  runyi_management_status?: string;
  runyi_management_scopes?: string[];
};

async function edgeFunctionErrorMessage(error: unknown, fallback: string) {
  if (error && typeof error === "object" && "context" in error) {
    const context = (error as { context?: unknown }).context;
    if (context instanceof Response) {
      const payload = await context.clone().json().catch(() => null) as {
        error?: string;
        detail?: string;
        stage?: string;
      } | null;
      if (payload?.error || payload?.detail) {
        const message = payload.detail || payload.error || fallback;
        return payload.stage ? `${message} [${payload.stage}]` : message;
      }
    }
  }
  return error instanceof Error ? error.message : fallback;
}

type BrokerResponse = {
  ok?: boolean;
  error?: string;
  detail?: string;
  enrollment?: TenantProfile;
  enrollment_status?: string;
  tenant_project_ref?: string;
  tenant_session?: {
    access_token: string;
    expires_in?: number;
    token_type?: string;
    user?: { id?: string; email?: string };
  };
  health?: TenantHealth | null;
};

export function MemberDataAuthPanel({
  profile,
  controlSupabase,
  tenantSupabase,
  tenantDataSupabase,
  centralEmail,
  tenantEmail,
  onAuthChanged,
  onAccessTokenChanged,
  onRefreshProfile,
}: {
  profile: MemberProjectRuntimeProfile | null;
  controlSupabase: SupabaseClient | null;
  tenantSupabase: SupabaseClient | null;
  tenantDataSupabase: SupabaseClient | null;
  centralEmail: string | null;
  tenantEmail: string | null;
  onAuthChanged: (email: string | null) => void;
  onAccessTokenChanged: (accessToken: string | null) => void;
  onRefreshProfile: () => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [installerCopied, setInstallerCopied] = useState(false);
  const [health, setHealth] = useState<TenantHealth | null>(null);
  const [tenantProfile, setTenantProfile] = useState<TenantProfile | null>(null);
  const automaticAttempt = useRef<string | null>(null);

  const dataPlaneInstalled = profile?.schema_version === "member-data-v2"
    || profile?.schema_version === "member-enterprise-v3"
    || profile?.schema_version === "member-control-tower-v4";
  const enterpriseInstalled = profile?.schema_version === "member-enterprise-v3"
    || profile?.schema_version === "member-control-tower-v4";
  const controlTowerInstalled = profile?.schema_version === "member-control-tower-v4";
  const enrollmentVerified = tenantProfile?.enrollment_status === "VERIFIED";

  const loadTenantProfile = useCallback(async () => {
    if (!controlSupabase || !centralEmail || !profile?.configured) {
      setTenantProfile(null);
      return null;
    }
    const { data, error } = await controlSupabase.rpc("runyi_tenant_profile");
    if (error) {
      setNotice(error.message);
      return null;
    }
    const next = (data ?? null) as TenantProfile | null;
    setTenantProfile(next);
    return next;
  }, [centralEmail, controlSupabase, profile?.configured]);

  const readHealth = useCallback(async () => {
    if (!tenantDataSupabase || !dataPlaneInstalled) return null;
    const { data, error } = await tenantDataSupabase.rpc("runyi_tenant_health");
    if (error) {
      setNotice(error.message);
      return null;
    }
    const next = (data ?? null) as TenantHealth | null;
    setHealth(next);
    return next;
  }, [dataPlaneInstalled, tenantDataSupabase]);

  async function applyBrokerSession(payload: BrokerResponse) {
    const accessToken = payload.tenant_session?.access_token;
    if (!accessToken) {
      throw new Error("RUNYI did not receive a brokered tenant access token.");
    }

    const brokeredEmail = payload.tenant_session?.user?.email ?? centralEmail;
    onAccessTokenChanged(accessToken);
    onAuthChanged(brokeredEmail ?? null);
    setHealth(payload.health ?? null);
    await loadTenantProfile();
    return accessToken;
  }

  async function enrollExistingSession(session: Session) {
    if (!controlSupabase) throw new Error("RUNYI central session is unavailable.");
    const { data, error } = await controlSupabase.functions.invoke("runyi-tenant-enroll", {
      body: {
        tenant_access_token: session.access_token,
        tenant_refresh_token: session.refresh_token,
      },
    });
    if (error) {
      throw new Error(await edgeFunctionErrorMessage(error, "Member enrollment failed."));
    }
    const payload = (data ?? {}) as BrokerResponse;
    if (payload.error) throw new Error(payload.detail || payload.error);
    await applyBrokerSession(payload);
    setNotice("One-time member enrollment completed. Future RUNYI logins use only the normal RUNYI email code.");
  }

  async function restoreFromBackend() {
    if (!controlSupabase) throw new Error("RUNYI central session is unavailable.");
    const { data, error } = await controlSupabase.functions.invoke("runyi-tenant-session", { body: {} });
    if (error) {
      throw new Error(await edgeFunctionErrorMessage(error, "Member data session could not be restored."));
    }
    const payload = (data ?? {}) as BrokerResponse;
    if (payload.error) {
      if (payload.error === "TENANT_REENROLL_REQUIRED") {
        throw new Error(payload.detail || "Member project re-enrollment is required.");
      }
      throw new Error(payload.detail || payload.error);
    }
    await applyBrokerSession(payload);
    setNotice("Member data session restored automatically from the verified backend enrollment.");
  }

  useEffect(() => {
    if (!profile?.configured || !dataPlaneInstalled || !controlSupabase || !tenantSupabase || !centralEmail) {
      automaticAttempt.current = null;
      return;
    }

    const key = `${profile.project_ref ?? "unknown"}:${centralEmail}`;
    if (automaticAttempt.current === key) return;
    automaticAttempt.current = key;

    let cancelled = false;
    void (async () => {
      setBusy(true);
      try {
        const mapping = await loadTenantProfile();
        if (cancelled) return;

        if (mapping?.enrollment_status === "VERIFIED") {
          await restoreFromBackend();
          return;
        }

        const { data } = await tenantSupabase.auth.getSession();
        if (cancelled) return;
        const existing = data.session;
        if (
          existing
          && existing.user.email?.trim().toLowerCase() === centralEmail.trim().toLowerCase()
        ) {
          await enrollExistingSession(existing);
          return;
        }

        onAccessTokenChanged(null);
        onAuthChanged(null);
        setHealth(null);
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Automatic tenant session restoration failed.";
          setNotice(message);
          onAccessTokenChanged(null);
          onAuthChanged(null);
          setHealth(null);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    centralEmail,
    controlSupabase,
    dataPlaneInstalled,
    loadTenantProfile,
    onAccessTokenChanged,
    profile?.configured,
    profile?.project_ref,
    tenantSupabase,
  ]);

  useEffect(() => {
    if (!tenantDataSupabase || !tenantEmail || !dataPlaneInstalled) {
      if (!busy) setHealth(null);
      return;
    }
    void readHealth();
  }, [busy, dataPlaneInstalled, readHealth, tenantDataSupabase, tenantEmail]);

  async function copyInstaller(version: "v2" | "v3" | "v4") {
    setBusy(true);
    setNotice(null);
    try {
      const filename = version === "v4"
        ? "runyi-member-control-tower-v4.sql"
        : version === "v3"
          ? "runyi-member-enterprise-v3.sql"
          : "runyi-member-data-v2.sql";
      const installerVersion = version === "v3"
        ? "20260914-enterprise-v3-hardened"
        : version === "v4"
          ? "20260914-control-tower-v4"
          : "20260914-member-data-v2";
      const response = await fetch(
        `${import.meta.env.BASE_URL}${filename}?v=${encodeURIComponent(installerVersion)}`,
        { cache: "no-store" },
      );
      if (!response.ok) throw new Error("Could not load the RUNYI migration.");
      const sql = await response.text();

      if (
        version === "v3"
        && (
          !/\nbegin;\s*\n/i.test(sql)
          || !sql.includes("grant select on public.runyi_schema_manifest to authenticated;")
          || !/\ncommit;\s*$/i.test(sql)
        )
      ) {
        throw new Error("A stale Enterprise v3 installer was detected. Refresh RUNYI before copying the migration.");
      }

      await navigator.clipboard.writeText(sql);
      setInstallerCopied(true);
      setNotice(
        version === "v4"
          ? "Control Tower v4 migration copied. Install it only after enterprise v3."
          : version === "v3"
            ? "Enterprise v3 migration copied. This is a NEW migration after member-data-v2; do not rerun v2."
            : "Member data-plane v2 SQL copied.",
      );
      window.setTimeout(() => setInstallerCopied(false), 2000);
    } catch {
      setNotice("The migration could not be copied on this device.");
    } finally {
      setBusy(false);
    }
  }

  async function sendCode() {
    if (!tenantSupabase || !centralEmail) {
      setNotice("Sign in to RUNYI and verify the member Supabase project first.");
      return;
    }
    const cleanEmail = centralEmail.trim().toLowerCase();
    setBusy(true);
    setNotice(null);
    const { error } = await tenantSupabase.auth.signInWithOtp({
      email: cleanEmail,
      options: { shouldCreateUser: true },
    });
    setBusy(false);
    setNotice(error
      ? error.message
      : "One-time member-project code sent to the same email as the RUNYI account.");
  }

  async function verifyCode() {
    if (!tenantSupabase || !controlSupabase || !centralEmail) return;
    const cleanEmail = centralEmail.trim().toLowerCase();
    const cleanCode = code.replace(/\D/g, "");
    if (cleanCode.length < 6 || cleanCode.length > 8) {
      setNotice("Enter the complete 6- to 8-digit code.");
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const { data, error } = await tenantSupabase.auth.verifyOtp({
        email: cleanEmail,
        token: cleanCode,
        type: "email",
      });
      if (error || !data.session) throw new Error(error?.message ?? "The member-project code could not be verified.");

      await enrollExistingSession(data.session);
      setCode("");
      await readHealth();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Member enrollment failed.");
    } finally {
      setBusy(false);
    }
  }

  async function refreshSession() {
    setBusy(true);
    setNotice(null);
    try {
      await restoreFromBackend();
      await readHealth();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Member data session could not be restored.");
    } finally {
      setBusy(false);
    }
  }

  if (!profile?.configured) return null;

  return (
    <Card className="border-foreground/10 bg-card py-0">
      <CardContent className="space-y-4 p-4 sm:space-y-5 sm:p-7">
        <div className="flex items-start gap-4">
          <div className={`grid size-8 shrink-0 place-items-center rounded-lg ${tenantEmail && dataPlaneInstalled ? "bg-emerald-400/10 text-emerald-300" : "bg-primary/10 text-primary"}`}>
            <Database className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">4. Member data plane</p>
              <Badge className={tenantEmail && dataPlaneInstalled ? "bg-emerald-400/10 text-emerald-300" : "bg-foreground/8 text-foreground/65"}>
                {tenantEmail && dataPlaneInstalled ? "DATA LIVE" : dataPlaneInstalled ? "ENROLLMENT" : "PHASE 2 NEEDED"}
              </Badge>
              {enrollmentVerified && <Badge className="bg-emerald-400/10 text-emerald-300">ONE-TIME ENROLLED</Badge>}
            </div>
            <p className="mt-1 text-sm leading-6 text-foreground/48">
              Central RUNYI Auth and the member-owned Supabase Auth are separate identities. After one verified tenant enrollment, the backend session broker restores member-data access after future RUNYI email-code logins.
            </p>
          </div>
        </div>

        {!dataPlaneInstalled ? (
          <div className="space-y-4 rounded-xl border border-primary/20 bg-primary/[0.055] p-4">
            <div>
              <p className="text-sm font-semibold text-primary">Install RUNYI member data plane v2</p>
              <p className="mt-1 text-xs leading-5 text-foreground/50">
                Install v2 only on a project that has not already installed it. Existing member-data-v2 projects must not rerun this migration.
              </p>
            </div>
            <Button onClick={() => void copyInstaller("v2")} disabled={busy} className="h-11 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
              {busy ? <LoaderCircle className="animate-spin" /> : installerCopied ? <Check /> : <ClipboardCopy />}
              {installerCopied ? "Migration copied" : "Copy Phase 2 migration"}
            </Button>
            <Button variant="outline" onClick={() => void onRefreshProfile()} className="h-11 w-full border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground">
              <ShieldCheck />Refresh installation status
            </Button>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <SessionStatus
                title="Central RUNYI session"
                primary={centralEmail ?? "Not signed in"}
                secondary="Recurring login challenge · RUNYI email OTP"
                live={Boolean(centralEmail)}
              />
              <SessionStatus
                title="Member data session"
                primary={tenantEmail ?? "Not active"}
                secondary={`${profile.project_ref ?? "No tenant"} · ${profile.schema_version ?? "unknown schema"}`}
                live={Boolean(tenantEmail)}
              />
            </div>

            {controlTowerInstalled ? (
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.055] p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">Enterprise Control Tower</p>
                <p className="mt-1 text-sm text-foreground/75">member-control-tower-v4 · M1/M2/M3/M4 actor governance + risk/approval metadata + tenant guardrails</p>
              </div>
            ) : enterpriseInstalled ? (
              <div className="space-y-3 rounded-xl border border-amber-300/20 bg-amber-300/[0.055] p-4">
                <div>
                  <p className="text-sm font-semibold text-amber-100">Control Tower v4 available</p>
                  <p className="mt-1 text-xs leading-5 text-foreground/50">
                    Install migration 004 after enterprise v3 to enable Owner ChatGPT M3 and RUNYI AI API M4 governance.
                  </p>
                </div>
                <Button variant="outline" onClick={() => void copyInstaller("v4")} disabled={busy} className="h-11 w-full border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground">
                  {busy ? <LoaderCircle className="animate-spin" /> : <ClipboardCopy />}Copy Control Tower v4 migration
                </Button>
              </div>
            ) : (
              <div className="space-y-3 rounded-xl border border-amber-300/20 bg-amber-300/[0.055] p-4">
                <div>
                  <p className="text-sm font-semibold text-amber-100">Enterprise v3 available</p>
                  <p className="mt-1 text-xs leading-5 text-foreground/50">
                    This is migration 003 after the existing member-data-v2 installation. It does not rerun or replace v2.
                  </p>
                </div>
                <Button variant="outline" onClick={() => void copyInstaller("v3")} disabled={busy} className="h-11 w-full border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground">
                  {busy ? <LoaderCircle className="animate-spin" /> : <ClipboardCopy />}Copy enterprise v3 migration
                </Button>
              </div>
            )}

            {enrollmentVerified ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.055] p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">Permanent backend enrollment verified</p>
                  <p className="mt-1 text-sm text-foreground/75">
                    Future normal logins require only the central RUNYI email code. Valid tenant refresh credentials remain server-side in Supabase Vault; Android and Web receive only a short-lived tenant access token.
                  </p>
                  <p className="mt-2 text-xs text-foreground/45">
                    Tenant user: {tenantProfile?.tenant_user_id ?? "verified"} · RUNYI management: {tenantProfile?.runyi_management_status ?? "NOT_CONNECTED"}
                  </p>
                </div>
                {!tenantEmail && (
                  <Button onClick={refreshSession} disabled={busy} className="h-11 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                    {busy ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}Restore member data session
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold">One-time tenant enrollment</p>
                  <p className="mt-1 text-xs leading-5 text-foreground/45">
                    RUNYI enforces an exact email match. The member-project code is sent only to {centralEmail ?? "the signed-in RUNYI email"}. After this enrollment, normal future logins use only the RUNYI email code.
                  </p>
                </div>
                <Button variant="outline" onClick={sendCode} disabled={busy || !tenantSupabase || !centralEmail} className="h-11 w-full border-foreground/12 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground">
                  {busy ? <LoaderCircle className="animate-spin" /> : <Mail />}Send one-time member-project code
                </Button>
                <div className="rounded-xl border border-foreground/10 bg-[var(--surface-inset)] p-4">
                  <p className="text-sm font-semibold">Enter the one-time member-project code</p>
                  <InputOTP
                    maxLength={8}
                    value={code}
                    onChange={(value) => setCode(value.replace(/\D/g, "").slice(0, 8))}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    containerClassName="mt-4 justify-center"
                    disabled={busy || !tenantSupabase}
                  >
                    <InputOTPGroup>
                      {Array.from({ length: 8 }, (_, index) => (
                        <InputOTPSlot key={index} index={index} className="h-12 w-8 border-foreground/15 bg-card text-lg sm:w-12" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                  <Button onClick={verifyCode} disabled={busy || !tenantSupabase || code.length < 6} className="mt-4 h-11 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                    {busy ? <LoaderCircle className="animate-spin" /> : <ShieldCheck />}Verify permanent enrollment
                  </Button>
                </div>
              </div>
            )}

            {health && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <Metric label="Movies" value={health.movie_count ?? 0} />
                  <Metric label="Runs" value={health.concept_run_count ?? 0} />
                  <Metric label="Characters" value={health.character_count ?? 0} />
                  <Metric label="Locations" value={health.location_count ?? 0} />
                  <Metric label="Files" value={health.file_count ?? 0} />
                </div>
                {health.enterprise_checks && (
                  <div className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4 text-xs leading-5 text-foreground/50">
                    Enterprise checks · missing objects {health.enterprise_checks.missing_required_objects ?? 0} · RLS gaps {health.enterprise_checks.protected_tables_without_rls ?? 0} · anon protected grants {health.enterprise_checks.anon_protected_table_grants ?? 0} · private storage {health.enterprise_checks.private_storage_bucket ? "PASS" : "FAIL"}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {busy && <div className="flex items-center gap-2 text-xs text-foreground/45"><LoaderCircle className="size-4 animate-spin" />RUNYI is verifying the member data identity…</div>}
        {notice && <div className="rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-100">{notice}</div>}
      </CardContent>
    </Card>
  );
}

function SessionStatus({
  title,
  primary,
  secondary,
  live,
}: {
  title: string;
  primary: string;
  secondary: string;
  live: boolean;
}) {
  return (
    <div className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4">
      <div className="flex items-center gap-2">
        <span className={`size-2 rounded-full ${live ? "bg-emerald-300" : "bg-foreground/25"}`} />
        <p className="text-xs font-semibold uppercase tracking-wider text-foreground/45">{title}</p>
      </div>
      <p className="mt-2 break-all text-sm font-medium text-foreground/80">{primary}</p>
      <p className="mt-1 text-xs text-foreground/40">{secondary}</p>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-foreground/8 bg-[var(--surface-inset)] p-3">
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-foreground/38">{label}</p>
    </div>
  );
}
